import TuneIcon from '@mui/icons-material/Tune';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, { useState, useEffect } from 'react';
import { ViewModule } from '@mui/icons-material';

type Props = {
  setIsSidePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSidePanelOpen: boolean;
  displayMode: string;
  setDisplayMode: React.Dispatch<React.SetStateAction<'grid' | 'list'>>;
  limit: number;
  setLimit: React.Dispatch<React.SetStateAction<number>>;
  orderBy: string;
  setOrderBy: React.Dispatch<React.SetStateAction<'date' | 'price'>>;
  order: string;
  setOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
};

function SearchUtilities(props: Props) {
  const location = window.location.pathname;
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Menu states
  const [limitAnchor, setLimitAnchor] = useState<null | HTMLElement>(null);
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);

  // Obsługa responsywności z debounce
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 200);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleLimitClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLimitAnchor(event.currentTarget);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortAnchor(event.currentTarget);
  };

  const handleLimitClose = () => setLimitAnchor(null);
  const handleSortClose = () => setSortAnchor(null);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3">
      <div className="w-full flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Limit selector */}
          <div className="relative">
            <button
              onClick={handleLimitClick}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors text-sm text-gray-700"
            >
              <span className="text-gray-500">Pokaż:</span>
              <span className="font-medium">{props.limit}</span>
              <KeyboardArrowDownIcon className="w-5 h-5 text-gray-400" />
            </button>
            <Menu
              anchorEl={limitAnchor}
              open={Boolean(limitAnchor)}
              onClose={handleLimitClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {[10, 20, 50].map((value) => (
                <MenuItem
                  key={value}
                  onClick={() => {
                    props.setLimit(value);
                    handleLimitClose();
                  }}
                  className={`text-sm ${props.limit === value ? 'text-blue-600 bg-blue-50' : ''}`}
                >
                  {value} produktów
                </MenuItem>
              ))}
            </Menu>
          </div>

          {/* Sort selector */}
          <div className="relative">
            <button
              onClick={handleSortClick}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors text-sm text-gray-700"
            >
              <span className="text-gray-500">Sortuj:</span>
              <span className="font-medium">
                {props.orderBy === 'price'
                  ? `Cena (${props.order === 'asc' ? '↑' : '↓'})`
                  : `Data (${props.order === 'asc' ? '↑' : '↓'})`}
              </span>
              <KeyboardArrowDownIcon className="w-5 h-5 text-gray-400" />
            </button>
            <Menu
              anchorEl={sortAnchor}
              open={Boolean(sortAnchor)}
              onClose={handleSortClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {[
                { value: 'price asc', label: 'Cena (od najniższej)' },
                { value: 'price desc', label: 'Cena (od najwyższej)' },
                { value: 'date desc', label: 'Najnowsze' },
                { value: 'date asc', label: 'Najstarsze' },
              ].map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => {
                    props.setOrderBy(
                      option.value.split(' ')[0] as 'date' | 'price',
                    );
                    props.setOrder(
                      option.value.split(' ')[1] as 'asc' | 'desc',
                    );
                    handleSortClose();
                  }}
                  className={`text-sm ${
                    props.orderBy + ' ' + props.order === option.value
                      ? 'text-blue-600 bg-blue-50'
                      : ''
                  }`}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {windowWidth <= 768 && location.includes('szukaj') && (
            <button
              onClick={() => props.setIsSidePanelOpen(!props.isSidePanelOpen)}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
              title="Filtry"
            >
              <TuneIcon className="w-5 h-5 text-gray-700" />
            </button>
          )}
          {windowWidth > 400 && (
            <button
              onClick={() =>
                props.setDisplayMode(
                  props.displayMode === 'grid' ? 'list' : 'grid',
                )
              }
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
              title={`Widok ${props.displayMode === 'grid' ? 'listy' : 'siatki'}`}
            >
              {props.displayMode === 'grid' ? (
                <ViewListIcon className="w-5 h-5 text-gray-700" />
              ) : (
                <ViewModule className="w-5 h-5 text-gray-700" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default SearchUtilities;
